import { BlogPost } from "@/types/blog";
import { PageSEO } from "@/components/PageSEO";
import { createBlogPostSchema, createBreadcrumbsSchema } from "@/utils/schemaUtils";
import { transformSingleUrl } from "@/utils/urlTransformer.js";

interface BlogPostSEOProps {
  post: BlogPost;
  ssrMode?: boolean;
}

export const BlogPostSEO = ({ post, ssrMode = false }: BlogPostSEOProps) => {
  if (!post) {
    return null;
  }

  // Genera il titolo SEO per l'articolo
  const seoTitle = `${post.title} - Blog Gildy`;
  
  // Genera la descrizione SEO (usa excerpt o tronca il contenuto)
  const seoDescription = post.excerpt 
    ? post.excerpt.length > 160 
      ? post.excerpt.substring(0, 157) + '...'
      : post.excerpt
    : post.content 
      ? post.content.replace(/<[^>]*>/g, '').substring(0, 157) + '...'
      : `Leggi l'articolo "${post.title}" sul blog di Gildy. Scopri guide, consigli e informazioni sui metalli preziosi.`;

  // Genera l'URL canonico
  const canonicalPath = `/blog/${post.slug}`;

  // Genera le keywords
  const keywords = [
    'blog oro',
    'blog argento', 
    'metalli preziosi',
    'oro investimento',
    'quotazioni oro',
    ...(post.categories || []),
    ...(post.tags || [])
  ];

  // Genera i breadcrumbs
  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: canonicalPath }
  ];

  // Aggiungi categoria ai breadcrumbs se presente
  if (post.categories && post.categories.length > 0) {
    breadcrumbItems.splice(2, 0, {
      name: `Categoria: ${post.categories[0]}`,
      url: `/blog/categoria/${post.categories[0]}`
    });
  }

  // Crea structured data per l'articolo (con immagine trasformata per uso interno)
  const blogPostSchemaProps = {
    title: post.title,
    description: seoDescription,
    datePublished: post.date,
    dateModified: post.modified,
    authorName: post.author?.name || 'Gildy',
    authorImage: post.author?.avatar,
    featuredImage: post.featuredImage ? transformSingleUrl(post.featuredImage, true, false) : post.featuredImage,
    slug: post.slug,
    categories: post.categories,
    tags: post.tags,
    wordCount: post.wordCount,
    readingTime: post.readingTime
  };
  
  const blogPostSchema = createBlogPostSchema(blogPostSchemaProps);
  const breadcrumbsSchema = createBreadcrumbsSchema(breadcrumbItems);
  
  const structuredData = [blogPostSchema, breadcrumbsSchema];

  // Calcola il tempo di lettura stimato
  const wordCount = post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200)) + ' min di lettura';

  // Genera l'immagine OG (usa featured image trasformata per meta tags o immagine di default)
  const ogImage = post.featuredImage 
    ? transformSingleUrl(post.featuredImage, true, true)
    : 'https://www.gildy.it/logo.png';

  return (
    <PageSEO
      title={seoTitle}
      description={seoDescription}
      canonicalPath={canonicalPath}
      ogImage={ogImage}
      structuredData={structuredData}
      keywords={keywords}
      ogType="article"
      author="Gildy"
      publishedTime={post.date}
      modifiedTime={post.modified}
      breadcrumbs={breadcrumbItems}
      articleSection={post.categories?.[0]}
      articleTags={post.tags}
      readingTime={readingTime}
      wordCount={wordCount}
      ssrMode={ssrMode}
    />
  );
};
